import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {observer} from 'mobx-react-lite';
import { Box, Card, CardActionArea, CardContent, CardMedia, Typography } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import { withRouter } from 'react-router-dom';
import LRAvatar from '../../../components/LRAvatar'; 
import 'react-placeholder/lib/reactPlaceholder.css';
import { RectShape } from 'react-placeholder/lib/placeholders';
import { useStore } from '../../../mobx-store';
import analytics from '../../../services/analytics';
import ClampLines from 'react-clamp-lines';
import { COLOR_BORDER_GREY_LIGHTER, COLOR_COMPLIANCE_GREY, COLOR_CHARLIE, COLOR_GRADE, COLOR_STEAK_SAUCE } from '../../../constants';
import { useOnboarding } from 'components/Onboarding';
import Logger from 'js-logger';
import { useBoardListpackHeroImages, useBoardListpackListings } from 'hooks/useBoardListpacks';

const Cointainer = styled.div`
    margin: 0 auto;
    width: 296px;
    min-height: 205px;
    display: flex;
    align-items: center;
    position: relative;
`;

const ShadowPaper = styled.div`
    border-radius: 4px
    position: absolute;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.35);
    width: 296px;
    min-height: 205px;
    height: 100%;
    background: #FFF
    z-index: 0;
    transform: rotate(-4deg);

    ${({ odd })=>
        odd === true
        && `
        transform: rotate(4deg);
    `}
`;

const StyledCard = styled(Card)`
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.35);
    width: 296px;
    z-index: 1;
`;

const StyledCardMedia = styled(CardMedia)`
    position: relative;
    margin: 16px 16px 0 16px;
    height: 136px;
    border-radius: 4px;
    background-color: ${COLOR_COMPLIANCE_GREY};
    width: ${(props)=> props?.width};
`;

const StyledCardMediaSecondary = styled(CardMedia)`
    position: absolute;
    height: 66px;
    border-radius: 4px;
    background-color: ${COLOR_COMPLIANCE_GREY};
    width: 49.5%;
    bottom: ${(props)=> props?.bottom};
    left: 178px;
`;

const StyledCardContent = styled(CardContent)`
    padding-top: 0;
    padding-bottom: 0;
    padding-right: 8px;
    min-height: 48px;
    display: flex;
`;

const StyledTypography = styled(Typography)`
    font-weight: 500;
    line-height: inherit;
    padding-right: 4px;
    padding-top: 12px;
    padding-bottom: 12px;
`;

const StyledTypographyMainText = styled(Typography)`
    line-height: 24px;
    padding-top: 12px;
    padding-bottom: 12px;
    color: ${COLOR_GRADE};
`;

const StyledHomeIcon = styled(HomeIcon)`
    width: 16px;
    height: 16px;
`;

const StyledNewListpackBadge = styled(Box)`
    background-color: ${COLOR_CHARLIE};
    padding: 1px 6px;
    display: inline-flex;
    margin-left: 8px;
    font-size: 12px;
    border-radius: 2px;
    height: 20px;
    align-items: center;
    vertical-align: middle;
`;

export const ListpackCard = withRouter(observer(({
    listpack, 
    index,
    history,
    ...props
})=> {
    const { UserStore, usersBoardRole, boardsStore } = useStore();
    const { activeBoard } = boardsStore;
    const { user } = UserStore;
    const isOddIndex = Boolean(index & 1);
    const affiliate = listpack.handSelected ? user?.affiliate : listpack?.affiliate;
    const formattedName = listpack.handSelected ? `${user?.affiliate?.first_name.split(' ')[0]}'s picks` : listpack?.name;
    const editUrl = `/boards/listpack/${listpack.id}/edit`;
    const listpackContentText = listpack.preference_display ? listpack.preference_display.join(', ') : '';
    const onboarding = useOnboarding();
    const { listings } = useBoardListpackListings(listpack);
    const listpackHeroImages = useBoardListpackHeroImages(
        listpack,
        listpack?.handSelected ? listpack.listpackCards : listings
    );
    const haveListpackHeroImages = listpackHeroImages?.length === 3;

    function handleClick() {
        if(listpack.handSelected && !listpack.id || (onboarding && onboarding.isNurtureFirstRunOnboarding && ['highlight-saved-search'].includes(onboarding.stepId))) return;

        if(listpack.newToCollaborator) {
            listpack.updateOpenedByCollaborators(user.id);
        }

        history.push(`/boards/listpack/${listpack.id}/listings`);
    }

    if(!listpack.listpackDataLoaded) {
        return <></>;
    }

    const listpackCardsCount = useMemo(()=> {
        return listpack?.handSelected ? (listpack?.listpackCards?.length || 0 ) : listpack?.activeListingsCount;
    },[listpack]);

    return <>
        {listpack.opened ? (
            <Box py="12px">
                <Cointainer>
                    <ShadowPaper odd={isOddIndex}/>
                    <StyledCard classes={{ root: 'tour-target-my-listpack-card-cta' }} onClick={handleClick}>
                        <CardActionArea>
                            <Box>
                                <StyledCardMedia
                                    image={listpackHeroImages[0] || null}
                                    width={haveListpackHeroImages ? '58.8%' : null}
                                >
                                    <Box
                                        position="absolute"
                                        borderRadius="4px 4px 0px 0px"
                                        bottom={0}
                                        minWidth={'190px'}
                                        width={1}
                                        height={30}
                                        style={{
                                            background: 'linear-gradient(180deg, #1A1A1A 0%, rgba(26, 26, 26, 0) 100%)',
                                            transform: 'rotate(-180deg)'
                                        }}
                                    />

                                    {
                                        (listpack.handSelected && listpack.newListingCards > 0) || (listpack?.newListingsCount && listpack?.newListingsCount > 0)  ? (
                                            <Box position="absolute" top={8} left={8} bgcolor="#FFF" display="flex" alignItems="center" borderRadius={4} px={1} py={0.5}>
                                                <Typography variant="caption" style={{color: COLOR_GRADE}}>
                                                    {listpack.handSelected ? listpack.newListingCards : listpack.newListingsCount} new <Typography variant="caption"><span role="img" aria-label="party">🎉</span></Typography>
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <></>
                                        )
                                    }

                                    {
                                        !listpack.handSelected && (listpack.activeListingsCount > 0) && (
                                            <Box
                                                position="absolute"
                                                bottom={8}
                                                left={8}
                                                color="#FFF"
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Box><HomeIcon fontSize="small"/></Box>
                                                <Box ml={0.5}>{listpackCardsCount}</Box>
                                            </Box>
                                        )
                                    }

                                    {
                                        listpack.handSelected && (listpack.listpackCards.length > 0) && (
                                            <Box
                                                position="absolute"
                                                bottom={8}
                                                left={8}
                                                color="#FFF"
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Box><HomeIcon fontSize="small"/></Box>
                                                <Box ml={0.5}>{listpack?.listpackCards?.length}</Box>
                                            </Box>
                                        )
                                    }

                                    {haveListpackHeroImages && listpackHeroImages[1] && (
                                        <StyledCardMediaSecondary
                                            image={listpackHeroImages[1] || null}
                                            bottom={'70px'}
                                        />
                                    )}

                                    {haveListpackHeroImages && listpackHeroImages[2] && (
                                        <StyledCardMediaSecondary
                                            image={listpackHeroImages[2] || null}
                                            bottom={'0px'}
                                        >
                                            <Box
                                                position="absolute"
                                                borderRadius="4px 4px 0px 0px"
                                                bottom={0}
                                                width={1}
                                                height={30}
                                                style={{
                                                    background: 'linear-gradient(180deg, #1A1A1A 0%, rgba(26, 26, 26, 0) 100%)',
                                                    transform: 'rotate(-180deg)'
                                                }}
                                            />

                                            {listpack.creator && !listpack.handSelected && (
                                                <Box
                                                    position="absolute"
                                                    bottom={8}
                                                    right={8}
                                                    bgcolor="#FFF"
                                                    display="flex"
                                                    alignItems="center"
                                                    borderRadius="50%"
                                                    p="2px"
                                                >
                                                    <LRAvatar user={listpack.creator} size="xsmall" />
                                                </Box>
                                            )}
                                        </StyledCardMediaSecondary>
                                    )}
                                </StyledCardMedia>
                            </Box>
                            <StyledCardContent>
                                {!listpack.handSelected ? (
                                    <>
                                        <Box
                                            width="100%"
                                            display="flex"
                                            flexDirection="column"
                                        >
                                            <Box display="flex" alignItems="center">
                                                <StyledTypographyMainText variant="h6">
                                                    {formattedName}
                                                    <Typography
                                                        variant="caption"
                                                        style={{
                                                            display: listpack.newToCollaborator ? 'inline-flex' : 'none',
                                                            position: 'relative',
                                                            bottom: '2px'
                                                        }}
                                                    >
                                                        <StyledNewListpackBadge>
                                                            New
                                                        </StyledNewListpackBadge>
                                                    </Typography>
                                                </StyledTypographyMainText>
                                            </Box>
                                            {
                                                // (!onboarding.isActive || (onboarding.isActive && !isMobile )) && (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    mb={2}
                                                    paddingY={1/2}
                                                >
                                                    <Box display="flex" color={COLOR_STEAK_SAUCE} maxWidth={'219px'}>
                                                        <Typography variant="caption">
                                                            <ClampLines
                                                                text={listpackContentText}
                                                                id="listpack-content-text"
                                                                lines={2}
                                                                ellipsis="..."
                                                                buttons={false}
                                                            />
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        display={!UserStore.isConsumer ? 'none' : 'flex'}
                                                        disabled={!UserStore.isConsumer}
                                                        px={'7px'}
                                                        height={'24px'}
                                                        onClick={(e)=> {
                                                            if((onboarding && onboarding.isNurtureFirstRunOnboarding && ['highlight-saved-search'].includes(onboarding.stepId))) return;
                                                            e.stopPropagation();
                                                            analytics.eventTrack('robin_saved_search_cover_edit_button_click', {
                                                                role: usersBoardRole,
                                                                board_id: activeBoard.id,
                                                                saved_search_id: listpack.id
                                                            });
                                                            history.push(editUrl);
                                                        }}
                                                        border={1}
                                                        borderColor={COLOR_BORDER_GREY_LIGHTER}
                                                        borderRadius={4}
                                                        mr={1}
                                                        color={COLOR_GRADE}
                                                        py={'4px'}
                                                        justifyContent="center"
                                                        alignItems="center"
                                                    >
                                                        <Typography variant="caption">
                                                            Edit
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                // )
                                            }
                                        </Box>
                                    </>
                                ) : (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        width="100%"
                                        pt={1}
                                        pb={1}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <LRAvatar user={affiliate} size="xsmall" />
                                            <Box ml={1} display="flex" alignItems="center">
                                                <StyledTypography variant="body1" style={{ padding: 0 }}>
                                                    {formattedName}
                                                </StyledTypography>
                                            </Box>
                                        </Box>
                                        <Box mr={1}>
                                            <svg style={{ display: 'block' }} width="61" height="38" viewBox="0 0 61 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M7.8126 13.341C8.17261 13.3521 8.53298 13.3416 8.89171 13.3096C9.02311 14.6584 8.27052 16.4555 8.99524 17.5841C9.54873 17.8043 9.74782 17.0061 9.8633 16.6246C9.34963 15.5943 9.8633 14.3713 9.75977 13.2506C11.691 12.7433 13.682 12.008 15.0797 10.4979C14.9124 12.6647 14.0643 15.0241 14.4147 17.124C14.9522 17.588 15.5814 16.9746 15.4221 16.3769C15.0717 14.0174 15.9836 11.5439 16.2185 9.1727C16.7999 8.0441 18.1219 5.91273 16.8038 4.91784C14.2594 5.31108 18.1975 10.1912 9.84737 12.3147C9.93498 10.3013 10.0266 8.26431 10.2734 6.26665C10.803 5.85375 10.4845 4.12742 9.62837 4.62684C9.03107 7.12785 9.2023 9.85694 8.92356 12.4091C8.64483 12.4995 8.21079 12.4405 7.91215 12.527C7.6135 12.6136 7.45422 13.2191 7.8126 13.341ZM20.7659 13.459C21.4154 12.811 22.0081 12.1098 22.5378 11.363C22.733 11.9765 21.9485 15.2207 24.1665 14.8708C25.8269 14.3281 27.0335 12.9046 28.0927 11.5872C28.0927 12.3737 27.4595 13.3175 27.9135 14.0017C28.1046 14.1197 28.5426 14.1079 28.5944 13.8287C28.9647 13.2073 31.1468 9.78615 31.7561 10.6827C32.0467 11.9018 30.8243 14.2494 32.6998 14.5837C34.1293 14.6348 35.2403 13.3725 36.2158 12.5074C36.3791 15.4134 39.4771 13.2939 40.2336 11.8153C40.3372 13.0225 40.9822 14.7921 42.4994 14.568C42.8975 14.4146 42.6945 13.7107 42.2803 13.7343C41.03 13.3764 41.026 11.5793 41.1136 10.5215C42.7701 7.61153 44.9522 4.68189 45.3186 1.28429C45.231 -0.681908 42.9294 0.00233073 42.36 1.33542C40.807 4.11169 40.5801 7.35986 40.1341 10.4193C37.3786 7.41491 35.7539 13.0736 33.2015 13.6163C31.7162 14.0096 33.0502 11.194 32.6361 10.435C31.7959 8.35476 29.6775 10.608 28.9488 11.658C29.0045 11.1114 29.347 10.5726 29.3111 10.0418C29.3111 9.64852 28.4431 9.45976 28.3913 9.91199C27.6347 10.4665 23.5851 16.2589 23.3979 12.9989C23.1112 11.8821 23.9355 10.5058 23.3621 9.53448C22.5219 9.16483 22.2551 10.4271 21.7693 10.8754C21.4866 11.3237 19.205 14.3556 19.0178 13.1444C19.0178 11.9647 19.3403 10.5923 20.3916 9.88053C20.8774 9.65245 21.391 10.0339 21.7972 10.2384C22.1357 10.3721 22.5179 9.91592 22.1954 9.65245C18.5997 5.80656 15.9836 17.6352 20.7659 13.4512V13.459ZM41.3088 8.13061C42.9015 -2.29419 47.3295 -0.945379 41.2729 8.65755C41.2928 8.4806 41.2729 8.30757 41.3088 8.13061ZM37.1118 12.2714C37.2432 10.8204 38.5413 9.66818 39.6363 11.194C39.1505 11.8113 36.9684 14.5129 37.1118 12.2714ZM60.6053 16.2314C60.3544 14.2652 58.1245 15.146 57.6586 16.5224C56.3406 19.4127 56.3804 22.6687 56.1893 25.7557C53.9873 23.4945 52.7529 26.668 51.3711 27.9853C50.0213 29.4639 46.4654 30.9386 46.2344 27.887C51.8569 26.4517 47.6918 21.2609 45.5416 26.5579C45.5097 26.5579 45.3584 26.7348 45.3902 26.7623C44.4266 28.2685 40.9504 33.4474 39.4731 30.0656C39.2979 28.992 38.9873 27.6393 39.6881 26.7309C40.8827 25.2326 40.6557 24.3714 42.9413 24.8001C43.2798 24.8237 43.463 24.5681 43.4749 24.2574C43.4749 23.3569 42.0494 23.9703 41.484 23.8878C42.4556 22.6097 42.364 19.7076 40.1739 21.1626C39.3775 21.9491 39.1505 23.1524 38.8877 24.1906C38.427 24.1692 37.9652 24.1956 37.51 24.2692C36.9445 24.1906 36.6499 25.036 37.3188 25.1147C37.7825 25.1258 38.2465 25.1114 38.7085 25.0714C38.6408 25.6849 38.5094 26.2865 38.4975 26.9039C31.6963 35.7597 29.5341 27.7848 33.7988 27.3758C34.2209 27.4505 34.8022 27.4073 34.7545 26.8449C34.3762 26.3612 33.4404 26.4517 32.875 26.5972C31.1906 27.1792 30.6769 29.1179 29.2673 30.0852C27.9135 31.4537 24.6722 32.4132 24.5288 29.6448C29.9443 28.3432 26.2809 23.058 23.8718 28.1544C23.816 28.2016 23.7523 28.2567 23.7802 28.2763C22.6613 29.3341 19.7067 33.7817 18.1099 31.611C17.0189 29.4403 18.5599 28.9055 19.3045 26.9826C20.1885 24.3361 22.2432 20.8441 20.75 18.1897C16.8237 17.1201 16.8078 26.5618 16.7322 29.0589C15.4778 31.0251 10.7592 34.9968 9.96285 30.8245C15.1991 29.6448 11.9538 24.2849 9.35361 29.1926C7.88185 30.0946 6.49105 31.1194 5.19646 32.2559C4.26468 30.7734 3.79879 31.147 5.33184 30.0223C6.40299 28.9252 4.65889 27.8359 3.76694 28.8426C2.52059 30.8088 4.16513 30.3094 1.09107 31.1548C0.868079 31.1981 0.585361 31.2532 0.617216 31.5481C0.617216 32.6845 2.80729 31.4891 3.36476 31.2885C3.43644 31.261 3.46033 31.2414 3.50413 31.2885C3.81573 31.831 4.15737 32.3561 4.52749 32.8615C3.23734 33.7778 1.52908 37.2343 4.3483 37.077C6.12823 36.6838 6.58616 34.3912 5.67031 33.0227C6.69937 32.0297 7.83267 31.1478 9.05098 30.392C8.91162 31.9059 9.95887 33.4828 11.5835 33.5379C13.7576 33.4435 15.4818 31.8627 16.8277 30.3172C17.0029 31.3672 17.3414 32.6295 18.4842 32.9991C20.7061 33.4317 22.3029 31.1195 23.6608 29.7313C23.6681 30.3997 23.9297 31.041 24.3937 31.5277C24.8578 32.0143 25.4902 32.3108 26.1654 32.3582C27.9931 32.2677 29.6098 31.1116 30.7884 29.7864C31.5291 34.3519 36.8928 30.5728 38.4139 28.4061C38.3581 29.8257 38.7284 31.7958 40.4048 32.0475C42.794 32.016 43.9647 30.1363 45.3982 28.3471C45.4863 28.9451 45.7795 29.4951 46.2288 29.9052C46.6782 30.3153 47.2565 30.5607 47.867 30.6004C49.6748 30.5139 51.2756 29.3774 52.4502 28.0758C52.7927 31.0093 55.7672 28.6735 56.4043 27.1123C56.6034 28.2921 57.3958 30.0144 58.8891 29.6802C59.2873 29.4914 59.0125 28.8072 58.5984 28.8662C57.3241 28.6066 57.1728 26.8174 57.1728 25.7557C58.5944 22.7238 60.5296 19.629 60.6132 16.2038L60.6053 16.2314ZM46.3778 26.7781C46.6087 26.318 46.7521 25.7635 47.206 25.4607C47.5564 24.9888 48.5838 24.9338 48.1497 25.7281C47.9706 26.0545 46.4056 27.3365 46.3778 26.7781ZM40.0067 23.408C39.9429 22.7631 41.5198 20.4823 41.1813 22.3069C40.8906 22.9518 40.7553 24.1709 39.8354 24.0293C39.8747 23.8178 39.932 23.61 40.0067 23.408ZM39.5846 25.0714C39.5846 24.8945 39.7518 24.922 39.8752 24.9259C39.7872 25.1069 39.6762 25.276 39.5447 25.4293C39.5479 25.3091 39.5613 25.1894 39.5846 25.0714ZM24.6642 28.5359C24.8952 28.0797 25.0385 27.5213 25.4964 27.2185C25.8429 26.7505 26.8702 26.6916 26.4362 27.4859C26.257 27.8123 24.6961 29.0943 24.6642 28.5359ZM10.0823 29.7156C10.3133 29.2555 10.4566 28.701 10.9106 28.3982C11.257 27.9263 12.2883 27.8674 11.8543 28.6617C11.6751 28.992 10.1142 30.274 10.0823 29.7195V29.7156ZM4.15717 29.8336C4.24079 29.6723 4.40803 29.0785 4.73853 29.2516C4.71066 29.4836 4.28459 29.7313 4.15717 29.8336ZM5.21637 34.9929C4.49962 37.3523 2.28167 36.1726 4.99736 33.6362C5.12683 33.8351 5.21314 34.0584 5.25083 34.2919C5.28853 34.5254 5.27679 34.7641 5.21637 34.9929ZM17.6321 28.1937C17.871 15.0516 23.7444 16.7033 17.6321 28.1937V28.1937ZM55.6955 26.5775C55.2734 27.1241 53.8081 29.7234 53.3064 28.1151C53.171 27.3286 53.6369 26.7623 53.9514 26.1096C54.5209 25.504 55.2934 26.0663 55.7433 26.5028C55.7294 26.5289 55.7134 26.5539 55.6955 26.5775ZM59.6775 17.1948C59.2793 19.5542 58.1245 21.6935 57.1728 23.8799C57.2059 21.8487 57.4989 19.8298 58.0449 17.8712C58.3515 16.6718 60.0597 14.5601 59.6854 17.1948H59.6775Z" fill="#231F20"/>
                                            </svg>
                                        </Box>
                                    </Box>
                                )
                                }
                            </StyledCardContent>
                        </CardActionArea>
                    </StyledCard>
                </Cointainer>
            </Box>
        ) : (
            <Box onClick={handleClick} bgcolor="#FFF" display="flex" px={2} py={1} justifyContent="space-between" alignItems="center" boxShadow="0px 1px 2px rgba(0, 0, 0, 0.35)" borderRadius="4px"
                style={{
                    cursor: !listpack.handSelected || (listpack.handSelected && listpack.id) ? 'pointer': 'auto'
                }}>
                {
                    listpack.handSelected ? (
                        <Box display="flex" flex={1} alignItems="center">
                            <LRAvatar user={affiliate} size="xsmall" />
                            <Box fontSize="body2.fontSize" fontWeight="normal" mx={1}>
                                {formattedName}
                            </Box>
                            <Box display="flex" alignItems="center">
                                <svg width="84" height="14" viewBox="0 0 84 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.66719 2.85697C5.49405 3.42739 8.75113 7.01714 1.87917 8.66449C1.95809 7.08107 2.03176 5.48289 2.23697 3.91422C2.67897 3.57984 2.41587 2.25212 1.71079 2.62585C1.21617 4.59283 1.35824 6.73193 1.12672 8.73825C0.895202 8.80709 0.526873 8.76284 0.290091 8.85135C-0.514971 9.43161 0.563706 9.60372 1.10041 9.47095C1.21091 10.5282 0.57423 11.9297 1.18987 12.8296C1.64765 13.0017 1.81076 12.3772 1.91074 12.0772C1.48453 11.2658 1.91074 10.3069 1.82129 9.42669C3.42615 9.02838 5.07836 8.4432 6.23597 7.26793C6.35699 8.17766 4.59427 13.1 6.34121 12.5001C6.50801 10.3966 6.78901 8.30232 7.1831 6.22543C7.66193 5.30586 8.76165 3.63393 7.66719 2.85697ZM30.5194 0.280222C27.2623 0.403159 27.3623 6.18117 26.8361 8.40878C24.5524 6.0484 23.2107 10.4938 21.0796 10.9216C19.8326 11.1724 20.9744 9.04805 20.6113 8.41861C19.9167 6.78602 18.1593 8.5563 17.5542 9.40211C17.687 8.9849 17.7872 8.55924 17.8541 8.12848C17.8541 7.83344 17.1332 7.67116 17.0911 8.02522C15.8809 9.00871 14.9548 11.0445 13.2079 11.2068C12.3344 10.7151 13.9656 6.33853 12.1555 8.06947C11.361 9.05297 10.6612 10.3708 9.41938 10.8282C9.05631 9.70207 10.0561 7.0909 11.6399 8.28584C12.787 8.18258 11.2715 7.05648 10.6822 7.24826C7.08839 8.63498 8.30387 14.8457 12.2608 9.1464C12.3976 9.5988 11.8293 12.2395 13.6131 11.9051C14.9548 11.4576 15.9546 10.3266 16.8386 9.30867C16.8701 9.86435 15.9914 11.492 17.1701 11.2019C17.8804 10.2184 18.5645 8.79726 19.8273 8.43828C20.243 9.42177 19.017 11.3888 20.6639 11.6691C21.8531 11.7084 22.7687 10.7151 23.5843 10.0365C23.7211 12.3182 26.2888 10.6561 26.9203 9.49554C27.0044 10.4397 27.5412 11.8313 28.7987 11.6641C29.1408 11.5461 28.9619 10.9904 28.6146 11.0101C27.2728 10.4889 27.378 8.42353 28.2831 7.53839C29.0829 5.72876 32.4347 1.96691 30.5194 0.280222ZM26.3572 9.03821C22.9318 13.4639 24.2525 6.45655 26.4151 8.98412L26.3572 9.03821ZM30.3036 1.96691C29.7774 3.77653 28.7251 5.40913 27.7727 7.02697C27.9379 5.43788 28.318 3.87478 28.904 2.37506C29.2039 1.43091 30.793 -0.0344953 30.3036 1.96691ZM83.4166 0.280222C80.1595 0.403159 80.2595 6.18117 79.7333 8.40878C78.1548 6.52047 76.7814 8.90052 75.5923 9.84959C74.3768 10.9216 71.3302 11.8461 71.3512 9.44636C76.0869 8.67924 73.014 4.35188 70.8671 8.36452C70.8671 8.36452 70.7093 8.48746 70.7303 8.51204C68.8782 11.2019 64.595 13.9852 65.7211 8.41861C66.8524 7.43512 66.9629 6.23526 68.8413 6.82044C69.1255 6.85978 69.2886 6.668 69.3202 6.42704C69.3886 5.72385 68.1573 6.11724 67.689 6.01398C68.6151 5.11408 68.6256 2.78813 66.7945 3.7962C66.0789 4.37155 65.8105 5.27144 65.5211 6.08774C65.1424 6.0427 64.7598 6.03446 64.3793 6.06315C63.9162 5.9648 63.6163 6.60407 64.1636 6.70734C64.5461 6.74755 64.931 6.76561 65.3159 6.76143C65.2159 7.25318 65.0686 7.70558 65.0054 8.19241C59.0543 14.5507 57.3495 8.49729 61.0959 8.25634C61.4379 8.33993 61.922 8.34485 61.922 7.90228C61.6379 7.49905 60.8696 7.49905 60.3803 7.58756C58.9543 7.94162 58.3913 9.42177 57.16 10.0905C55.9446 11.074 53.1979 11.6248 53.2715 9.44636C57.8388 8.77267 55.1711 4.40105 52.8401 8.23175C52.7875 8.26617 52.7243 8.31043 52.7454 8.3301C51.3983 9.5988 47.6677 13.2771 47.573 9.26442C49.2778 6.88437 51.0405 4.12567 51.2773 1.12111C49.0884 -3.35378 46.5995 6.90404 46.8574 8.49729C45.684 9.97253 41.4903 12.7656 41.122 9.45128C45.5419 8.85135 43.2267 4.46006 40.7326 8.1334C39.4542 8.7463 38.2343 9.46029 37.0861 10.2676C36.4442 9.1464 36.0338 9.28409 37.3597 8.54647C38.3227 7.75476 36.9546 6.79094 36.1495 7.50888C35.1708 8.60548 36.1811 8.80709 34.1185 9.08739C33.8606 9.17098 33.3713 9.08739 33.3555 9.42177C33.3239 10.3217 35.134 9.51029 35.6497 9.39227C36.0285 9.57913 36.1758 10.3758 36.4968 10.7052C35.3708 11.3543 33.7186 13.9508 36.0601 14.0048C37.565 13.7983 38.1017 12.0379 37.4334 10.9118C38.3532 10.1999 39.3516 9.58177 40.4116 9.06772C40.1906 10.243 40.9378 11.5264 42.2901 11.6887C44.0896 11.7526 45.6261 10.6266 46.8468 9.50045C46.9152 10.3364 47.1099 11.3445 48.036 11.7035C49.8356 12.1952 51.3141 10.4741 52.5402 9.48078C52.5021 10.0038 52.6757 10.521 53.0266 10.93C53.3774 11.3389 53.8799 11.6098 54.4344 11.6887C55.9498 11.7379 57.3653 10.9364 58.4334 9.97253C58.7386 13.5918 63.4322 10.956 64.8371 9.3726C64.6897 10.479 64.8371 12.0477 66.242 12.3526C68.2152 12.4854 69.3202 11.0839 70.6251 9.77583C70.6559 10.249 70.8599 10.6978 71.2029 11.047C71.5459 11.3962 72.007 11.6245 72.5088 11.6936C74.0137 11.7428 75.4134 10.9511 76.4762 10.0119C76.5657 12.328 79.1808 10.6856 79.8123 9.52012C79.8964 10.4643 80.4332 11.8559 81.696 11.6887C82.038 11.5707 81.8591 11.015 81.5118 11.0347C80.1701 10.5135 80.2753 8.44812 81.1803 7.56298C81.9801 5.72876 85.3319 1.96691 83.4166 0.280222ZM71.5617 8.60548C71.6143 8.03013 73.5191 6.57457 73.0982 7.89245C72.8298 8.1629 71.6248 9.01363 71.5617 8.60548ZM66.5209 5.56157C66.5209 5.0354 67.9521 3.40281 67.5732 4.77478C67.2891 5.26652 67.0944 6.20576 66.342 6.03856C66.3797 5.87615 66.4307 5.71668 66.4946 5.56157H66.5209ZM66.0578 6.83519C66.0578 6.6975 66.2051 6.73193 66.3104 6.74176C66.2222 6.8768 66.1181 7.00216 65.9999 7.11549C66.0024 7.02142 66.013 6.92769 66.0315 6.83519H66.0578ZM53.482 8.60548C53.5346 8.03013 55.4394 6.57457 55.0185 7.89245C54.7501 8.1629 53.5452 9.01363 53.4557 8.60548H53.482ZM47.694 7.50888C49.1884 -2.89645 53.1926 0.0786064 47.694 7.88753C47.6782 7.75967 47.6677 7.63674 47.6782 7.50888H47.694ZM41.3114 8.60548C41.364 8.03013 43.2688 6.57457 42.8478 7.89245C42.6216 8.1629 41.4008 9.01363 41.3114 8.60548ZM36.4021 8.32026C36.7967 7.51872 37.2335 7.96129 36.4021 8.32026V8.32026ZM36.9283 12.4263C36.8851 12.6654 36.7584 12.8843 36.568 13.0489C36.3776 13.2135 36.1342 13.3145 35.8759 13.3361C35.0656 12.8443 36.4021 11.7428 36.8651 11.3691C36.9504 11.5326 36.9994 11.7105 37.0094 11.8922C37.0193 12.0739 36.9899 12.2555 36.923 12.4263H36.9283ZM79.2598 9.03821C77.8917 11.0495 76.1921 11.0052 77.8549 8.57105C78.381 8.11373 78.9756 8.61039 79.3176 8.98412L79.2598 9.03821ZM83.2009 1.96691C82.6747 3.77653 81.6223 5.40913 80.6699 7.02697C80.8345 5.43732 81.2165 3.87393 81.8065 2.37506C82.0854 1.43091 83.6902 -0.0344953 83.2009 1.96691Z" fill="#231F20"/>
                                </svg>
                            </Box>
                        </Box>
                    ) : (
                        <Box fontSize="body2.fontSize" fontWeight="normal" flex="1">
                            { formattedName }
                        </Box>
                    )
                }

                <Box color="lr_colors.grey_dark" display="flex" alignItems="center" width={55}>
                    <StyledHomeIcon fontSize="small"/>
                    <Box component="span" ml={0.5}>{listpackCardsCount}</Box>
                </Box>
            </Box>
        )}
    </>;
}));


export function MinimizedCardPlaceholder(props) {

    const [textWidth, /* setTextWidth */] = useState(Math.random() * (90 - 40) + 40);

    return (
        <Box  className="show-loading-animation" bgcolor="#FFF" display="flex" px={2} py={1} justifyContent="space-between" alignItems="center" boxShadow="0px 1px 2px rgba(0, 0, 0, 0.35)" borderRadius="4px">
            <Box fontSize="body2.fontSize" fontWeight="normal" flex="1">
                <RectShape style={{ height: '14px', backgroundColor: 'rgb(205, 205, 205)',  maxWidth: `${textWidth}%` }} />
            </Box>
            <Box color="lr_colors.grey_dark" display="flex" width={55}>
                <>
                    <StyledHomeIcon fontSize="small"/>
                    <Box ml={0.5} display="flex" alignItems="center" >
                        <RectShape style={{ height: '14px', backgroundColor: 'rgb(205, 205, 205)', width: '20px' }} />
                    </Box>
                </>
            </Box>
        </Box>);
}

export function CardPlaceholder(props) {
    return (
        <Cointainer className="show-loading-animation">
            <ShadowPaper/>
            <StyledCard >
                <CardActionArea>
                    <StyledCardMedia></StyledCardMedia>
                    <StyledCardContent>
                        <Box mt="12px" pr={1} height="100%" width="100%">
                            <RectShape style={{ height: '14px', backgroundColor: 'rgb(205, 205, 205)', maxWidth: '60%', margin: '0 auto' }} />
                        </Box>
                    </StyledCardContent>
                </CardActionArea>
            </StyledCard>
        </Cointainer>
    );
}
